/**
 * Purchase Order Screen (4.3.1) — Procurement Module - Orders
 * Real backend: M_PurchaseOrderVoucher / T_PurchaseOrderVoucher via
 * PROC_InsertPurchaseOrderVoucher (see /api/PurchaseOrder/api/v1/*).
 * Supplier search: /api/supplier-master (real VendorMaster, 2394 rows).
 * Item search: /api/document-hub/items (real ItemMaster, 249k rows).
 * Import: PDF/image → Sarvam OCR (/api/document-hub/analyze, category=purchase_order),
 *         Excel/CSV → parsed client-side with the `xlsx` library.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Search, RefreshCw, AlertCircle, Trash2, CheckCircle2, Package, X } from 'lucide-react';
import { API_BASE as API_URL, getToken } from '../../../lib/api';
import { useAttributeEngine } from '../../../hooks/useAttributeEngine';
import { useT, LanguageSwitcher } from '../../../lib/manchala-i18n.react.js';

const PO_ATTR_GROUPS = ['grp_classification', 'grp_style'];

const emptyItem = (aeAttrIds = []) => ({
  id: Date.now() + Math.random(),
  itemCode: '', itemDesc: '', hsnCode: '',
  qty: '', uom: 'PCS', rate: '', mrp: '', value: '', gstPercent: '',
  ...Object.fromEntries(aeAttrIds.map(id => [id, ''])),
});

const PurchaseOrderScreen = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const token = getToken();
  const t = useT();
  const { getGroup, toLookupOptions, loading: aeLoading, itemMasterAttrs } = useAttributeEngine();

  // AE classification attrs for item grid (grp_classification + grp_style with ItemMasterColumn)
  const aeItemAttrs = useMemo(() =>
    itemMasterAttrs.filter(a => PO_ATTR_GROUPS.includes(a.GroupId)),
    [itemMasterAttrs]
  );
  const aeAttrIds = useMemo(() => aeItemAttrs.map(a => a.AttributeId), [aeItemAttrs]);

  const [formData, setFormData] = useState({
    poNo: '', poDate: new Date().toISOString().split('T')[0], poRef: '', payMode: 'Credit', remarks: '',
    supplierId: '', freight: '0', salesTax: '0', others: '0',
  });
  const [supplierSearch, setSupplierSearch] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showSupplierList, setShowSupplierList] = useState(false);

  const [items, setItems] = useState([]);
  const [itemSearchByRow, setItemSearchByRow] = useState({}); // rowId -> {query, results}
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedPoNo, setSavedPoNo] = useState('');
  const itemDebounce = useRef({});

  const bgColor = isDark ? 'bg-[#1a1a2e]' : 'bg-gray-100';
  const cardBg = isDark ? 'bg-[#16213e]' : 'bg-white';
  const inputBg = isDark ? 'bg-[#0f0f1a]' : 'bg-gray-50';
  const borderColor = isDark ? 'border-[#1f4068]' : 'border-gray-300';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const labelColor = isDark ? 'text-gray-300' : 'text-gray-700';
  const sectionBg = isDark ? 'bg-[#1f2937]' : 'bg-gray-50';

  const loadSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/supplier-master`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setSuppliers(d.data || []);
    } catch { setError('Failed to load suppliers'); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);

  const filteredSuppliers = suppliers.filter(s => s.SupplierName?.toLowerCase().includes(supplierSearch.toLowerCase())).slice(0, 40);

  const pickSupplier = (s) => {
    setSelectedSupplier(s);
    setFormData(p => ({ ...p, supplierId: s.SupplierId }));
    setSupplierSearch(s.SupplierName);
    setShowSupplierList(false);
  };

  // ── Item grid helpers ────────────────────────────────────────────────────
  const addBlankRow = () => setItems(prev => [...prev, emptyItem(aeAttrIds)]);
  const removeRow = (id) => setItems(prev => prev.filter(it => it.id !== id));
  const updateRow = (id, patch) => setItems(prev => prev.map(it => it.id === id ? { ...it, ...patch, value: (parseFloat(patch.qty ?? it.qty) || 0) * (parseFloat(patch.rate ?? it.rate) || 0) || it.value } : it));

  const searchItemForRow = (rowId, query) => {
    setItemSearchByRow(prev => ({ ...prev, [rowId]: { query, results: prev[rowId]?.results || [] } }));
    clearTimeout(itemDebounce.current[rowId]);
    if (query.trim().length < 2) { setItemSearchByRow(prev => ({ ...prev, [rowId]: { query, results: [] } })); return; }
    itemDebounce.current[rowId] = setTimeout(() => {
      fetch(`${API_URL}/api/document-hub/items?q=${encodeURIComponent(query)}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(d => setItemSearchByRow(prev => ({ ...prev, [rowId]: { query, results: d.data || [] } }))).catch(() => {});
    }, 350);
  };

  const resolveItemForRow = (rowId, it) => {
    updateRow(rowId, { itemCode: it.Itemcode, itemDesc: it.ItemDesc, hsnCode: it.HSNCode || '' });
    setItemSearchByRow(prev => ({ ...prev, [rowId]: { query: it.ItemDesc, results: [] } }));
  };

  // ── Totals ───────────────────────────────────────────────────────────────
  const totalQty = items.reduce((s, i) => s + (parseFloat(i.qty) || 0), 0);
  const totalValue = items.reduce((s, i) => s + (parseFloat(i.value) || 0), 0);
  const freight = parseFloat(formData.freight) || 0;
  const salesTax = parseFloat(formData.salesTax) || 0;
  const others = parseFloat(formData.others) || 0;
  const netAmount = totalValue + freight + salesTax + others;

  // ── Import (PDF/Image via Sarvam OCR, Excel/CSV parsed client-side) ─────
  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError(null);
    if (!formData.supplierId) { setError('Please select a supplier first'); return; }
    const unresolved = items.filter(it => !it.itemCode);
    if (!items.length) { setError('Add at least one item to the Purchase Order'); return; }
    if (unresolved.length) { setError(`${unresolved.length} item row(s) still need an Item Master match — search and select an item for each.`); return; }
    setSaving(true);
    try {
      const payload = {
        VendorId: formData.supplierId, PODate: formData.poDate, Remarks: formData.remarks,
        Freight: freight, SalesTax: salesTax, Others: others,
        items: items.map(it => ({ ItemCode: it.itemCode, description: it.itemDesc, HSN: it.hsnCode, qty: it.qty, uom: it.uom, rate: it.rate, amount: it.value })),
      };
      const r = await fetch(`${API_URL}/api/PurchaseOrder/api/v1/Save`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) });
      const d = await r.json();
      if (!d.success) throw new Error(d.message || 'Save failed');
      setSavedPoNo(d.data?.PONo || '');
      setFormData(p => ({ ...p, poNo: d.data?.PONo || '' }));
    } catch (e) {
      setError(e.message || 'Failed to save Purchase Order');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setItems([]); setSelectedSupplier(null); setSupplierSearch(''); setSavedPoNo('');
    setFormData(p => ({ ...p, supplierId: '', poRef: '', remarks: '', freight: '0', salesTax: '0', others: '0' }));
  };

  const InputField = ({ label, name, value, onChange, type = 'text', disabled, className = '' }) => (
    <div className={`flex items-center ${className}`}>
      <label className={`w-20 text-xs ${labelColor}`}>{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(name, e.target.value)} disabled={disabled}
        className={`flex-1 px-2 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor} ${disabled ? 'opacity-60' : ''}`}
        data-testid={`input-${name}`} />
    </div>
  );

  return (
    <div className={`min-h-screen ${bgColor} ${textColor} p-4`} data-testid="purchase-order-screen">
      <div className={`${cardBg} rounded-lg p-3 mb-4 border ${borderColor}`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">Purchase Order</h1>
            <p className="text-xs text-gray-400">Screen ID: 4.3.1 | Procurement Module {savedPoNo && <span className="text-emerald-400 font-semibold">— Saved as {savedPoNo}</span>}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-gray-400 italic">To import from PDF/Excel/CSV, use the Document Hub in the top header</span>
            <button onClick={loadSuppliers} className="p-2 rounded bg-blue-600 hover:bg-blue-700 text-white" data-testid="refresh-btn">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2" data-testid="po-error-banner">
          <AlertCircle className="text-red-400" size={18} />
          <span className="text-red-400 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300"><X size={14} /></button>
        </div>
      )}

      <div className={`${cardBg} rounded-lg border ${borderColor} p-4`}>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className={`${sectionBg} rounded-lg p-3 border ${borderColor}`}>
            <h3 className={`text-xs font-semibold mb-3 pb-1 border-b ${borderColor}`}>Document Information</h3>
            <div className="space-y-2">
              <InputField label="P.O No" name="poNo" value={formData.poNo || '(auto on save)'} disabled onChange={() => {}} />
              <InputField label="P.O Date" name="poDate" value={formData.poDate} onChange={(n, v) => setFormData(p => ({ ...p, [n]: v }))} type="date" />
              <InputField label="P.O Ref" name="poRef" value={formData.poRef} onChange={(n, v) => setFormData(p => ({ ...p, [n]: v }))} />
            </div>
          </div>

          <div className={`${sectionBg} rounded-lg p-3 border ${borderColor}`}>
            <h3 className={`text-xs font-semibold mb-3 pb-1 border-b ${borderColor}`}>Supplier Information</h3>
            <div className="relative">
              <div className="flex items-center gap-2">
                <Search size={12} className="text-gray-400 flex-shrink-0" />
                <input value={supplierSearch} onFocus={() => setShowSupplierList(true)}
                  onChange={(e) => { setSupplierSearch(e.target.value); setShowSupplierList(true); setSelectedSupplier(null); }}
                  placeholder="Search supplier (2394 available)..." data-testid="po-supplier-search"
                  className={`flex-1 px-2 py-1.5 text-xs rounded border ${selectedSupplier ? 'border-emerald-500' : borderColor} ${inputBg} ${textColor}`} />
                {selectedSupplier && <CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0" />}
              </div>
              {showSupplierList && supplierSearch && !selectedSupplier && (
                <div className={`absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded border ${borderColor} ${cardBg} shadow-xl`}>
                  {filteredSuppliers.map((s, i) => (
                    <div key={i} onClick={() => pickSupplier(s)} data-testid={`po-supplier-option-${i}`}
                      className={`px-3 py-1.5 text-xs cursor-pointer hover:${isDark ? 'bg-[#0f0f1a]' : 'bg-gray-100'} border-t first:border-t-0 ${borderColor}`}>
                      {s.SupplierName} <span className="text-gray-500">({s.SupplierId})</span>
                    </div>
                  ))}
                  {filteredSuppliers.length === 0 && <div className="px-3 py-2 text-xs text-gray-500">No matches</div>}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={`${sectionBg} rounded-lg p-3 border ${borderColor} mb-4`}>
          <div className="flex items-center justify-between mb-3 pb-1 border-b ${borderColor}">
            <h3 className="text-xs font-semibold">Item Info</h3>
            <button onClick={addBlankRow} data-testid="po-add-item-row-btn" className="px-2 py-1 text-xs rounded bg-emerald-600 hover:bg-emerald-700 text-white">+ Add Row</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className={isDark ? 'bg-[#0f0f1a]' : 'bg-gray-100'}>
                <tr>
                  <th className="px-2 py-1 text-left">Item (search Item Master)</th>
                  <th className="px-2 py-1 text-left">HSN</th>
                  {aeItemAttrs.map(attr => (
                    <th key={attr.AttributeId} className="px-2 py-1 text-left whitespace-nowrap">
                      {t(`attr.${attr.AttributeId}`, attr.Label)}
                    </th>
                  ))}
                  <th className="px-2 py-1 text-right">Qty</th>
                  <th className="px-2 py-1 text-left">UOM</th>
                  <th className="px-2 py-1 text-right">Rate</th>
                  <th className="px-2 py-1 text-right">GST %</th>
                  <th className="px-2 py-1 text-right">Value</th>
                  <th className="px-2 py-1 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const rowSearch = itemSearchByRow[item.id];
                  return (
                    <tr key={item.id} className={`border-t ${borderColor}`} data-testid={`po-item-row-${item.id}`}>
                      <td className="px-2 py-1 relative" style={{ minWidth: 220 }}>
                        <div className="flex items-center gap-1">
                          <Package size={11} className={item.itemCode ? 'text-emerald-400' : 'text-gray-500'} />
                          <input value={rowSearch?.query ?? item.itemDesc} onChange={(e) => searchItemForRow(item.id, e.target.value)}
                            placeholder="Search Item Master..." data-testid={`po-item-search-${item.id}`}
                            className={`flex-1 px-1.5 py-1 text-xs rounded border ${item.itemCode ? 'border-emerald-500' : borderColor} ${inputBg} ${textColor}`} />
                        </div>
                        {rowSearch?.results?.length > 0 && (
                          <div className={`absolute z-10 mt-1 w-64 max-h-40 overflow-y-auto rounded border ${borderColor} ${cardBg} shadow-xl`}>
                            {rowSearch.results.map((it, oi) => (
                              <div key={oi} onClick={() => resolveItemForRow(item.id, it)} data-testid={`po-item-option-${item.id}-${oi}`}
                                className="px-2 py-1 text-xs cursor-pointer hover:bg-black/20 border-t first:border-t-0 border-white/10">
                                {it.ItemDesc} <span className="text-gray-500">({it.Itemcode})</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1"><input value={item.hsnCode} onChange={(e) => updateRow(item.id, { hsnCode: e.target.value })} className={`w-16 px-1 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`} /></td>
                      {aeItemAttrs.map(attr => {
                        const opts = toLookupOptions(attr.AttributeId);
                        const isSelect = attr.ControlType === 'LookUpEdit' || attr.ControlType === 'ComboBoxEdit';
                        const val = item[attr.AttributeId] || '';
                        return (
                          <td key={attr.AttributeId} className="px-2 py-1">
                            {isSelect ? (
                              <select value={val} onChange={e => updateRow(item.id, { [attr.AttributeId]: e.target.value })}
                                className={`px-1 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`}
                                style={{ minWidth: 80 }}>
                                <option value="">{aeLoading ? '…' : '—'}</option>
                                {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            ) : (
                              <input value={val} onChange={e => updateRow(item.id, { [attr.AttributeId]: e.target.value })}
                                className={`w-20 px-1 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`} />
                            )}
                          </td>
                        );
                      })}
                      <td className="px-2 py-1"><input value={item.qty} onChange={(e) => updateRow(item.id, { qty: e.target.value })} className={`w-16 px-1 py-1 text-xs text-right rounded border ${borderColor} ${inputBg} ${textColor}`} /></td>
                      <td className="px-2 py-1"><input value={item.uom} onChange={(e) => updateRow(item.id, { uom: e.target.value })} className={`w-14 px-1 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`} /></td>
                      <td className="px-2 py-1"><input value={item.rate} onChange={(e) => updateRow(item.id, { rate: e.target.value })} className={`w-20 px-1 py-1 text-xs text-right rounded border ${borderColor} ${inputBg} ${textColor}`} /></td>
                      <td className="px-2 py-1"><input value={item.gstPercent} onChange={(e) => updateRow(item.id, { gstPercent: e.target.value })} className={`w-14 px-1 py-1 text-xs text-right rounded border ${borderColor} ${inputBg} ${textColor}`} /></td>
                      <td className="px-2 py-1 text-right font-mono">{(parseFloat(item.value) || 0).toFixed(2)}</td>
                      <td className="px-2 py-1 text-center">
                        <button onClick={() => removeRow(item.id)} data-testid={`po-remove-row-${item.id}`} className="p-1 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30"><Trash2 size={10} /></button>
                      </td>
                    </tr>
                  );
                })}
                  {items.length === 0 && <tr><td colSpan={8 + aeItemAttrs.length} className="px-2 py-6 text-center text-gray-500">No items yet — click "+ Add Row" or use Import above.</td></tr>}
                <tr className={`border-t-2 ${borderColor} font-semibold ${isDark ? 'bg-[#0f0f1a]' : 'bg-gray-100'}`}>
                  <td colSpan={2 + aeItemAttrs.length} className="px-2 py-1 text-right">Total:</td>
                  <td className="px-2 py-1 text-right">{totalQty.toFixed(3)}</td>
                  <td colSpan={3}></td>
                  <td className="px-2 py-1 text-right">{totalValue.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className={`${sectionBg} rounded-lg p-3 border ${borderColor}`}>
            <h3 className={`text-xs font-semibold mb-3 pb-1 border-b ${borderColor}`}>Bill Details</h3>
            <div className="space-y-2">
              <InputField label="Freight" name="freight" value={formData.freight} onChange={(n, v) => setFormData(p => ({ ...p, [n]: v }))} />
              <InputField label="Sales Tax" name="salesTax" value={formData.salesTax} onChange={(n, v) => setFormData(p => ({ ...p, [n]: v }))} />
              <InputField label="Others" name="others" value={formData.others} onChange={(n, v) => setFormData(p => ({ ...p, [n]: v }))} />
            </div>
          </div>
          <div className={`${sectionBg} rounded-lg p-3 border ${borderColor}`}>
            <h3 className={`text-xs font-semibold mb-3 pb-1 border-b ${borderColor}`}>Amount Details</h3>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between"><span className={labelColor}>Item Value:</span><span>{totalValue.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className={labelColor}>Freight + Tax + Others:</span><span>{(freight + salesTax + others).toFixed(2)}</span></div>
              <div className="flex justify-between font-semibold text-sm pt-1 border-t border-white/10"><span className={labelColor}>Net Amount:</span><span className="text-emerald-400" data-testid="po-net-amount">{netAmount.toFixed(2)}</span></div>
            </div>
          </div>
        </div>

        <div className={`${sectionBg} rounded-lg p-3 border ${borderColor} mb-4`}>
          <div className="flex items-start gap-2">
            <label className={`w-20 text-xs ${labelColor}`}>Remarks</label>
            <textarea value={formData.remarks} onChange={(e) => setFormData(p => ({ ...p, remarks: e.target.value }))} rows={2}
              className={`flex-1 px-2 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`} data-testid="input-remarks" />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher theme="dark" data-testid="po-lang-switcher" />
          <button onClick={handleSave} disabled={saving} data-testid="po-save-btn"
            className="px-4 py-2 text-xs rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold">
            {saving ? 'Saving...' : 'Save Purchase Order'}
          </button>
          <button onClick={handleReset} data-testid="po-new-btn" className="px-3 py-2 text-xs rounded border border-white/20 hover:bg-white/5 text-white">New</button>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderScreen;
